import React, { useState, useEffect, useMemo } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { useSearchParams } from 'react-router-dom';
import { usersAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  Mail,
  User,
  Loader2,
  CheckCircle2,
  XCircle,
  Key,
  Eye,
  EyeOff,
  Shield,
  UserCog,
  Archive,
  ArchiveRestore,
  Copy,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { PflichtKarte } from '../components/verwaltungskonten/PflichtKarte';
import { KontoTabelle } from '../components/verwaltungskonten/KontoTabelle';
import { KontoAktionDialog } from '../components/verwaltungskonten/KontoDialoge';
import { KontoDetails } from '../components/verwaltungskonten/KontoDetails';
import { istGesperrt, passtZusatzfilter, zusatzAnzahlen } from '../lib/verwaltungskonten';

// Password strength checker
const checkPasswordStrength = (password) => {
  let strength = 0;
  let feedback = [];
  
  if (password.length >= 8) {
    strength += 25;
  } else {
    feedback.push('Mindestens 8 Zeichen');
  }
  
  if (/[a-z]/.test(password)) {
    strength += 25;
  } else {
    feedback.push('Kleinbuchstaben');
  }
  
  if (/[A-Z]/.test(password)) {
    strength += 25;
  } else {
    feedback.push('Großbuchstaben');
  }
  
  if (/[0-9]/.test(password) || /[^a-zA-Z0-9]/.test(password)) {
    strength += 25;
  } else {
    feedback.push('Zahlen oder Sonderzeichen');
  }
  
  let label = 'Sehr schwach';
  let color = 'bg-red-500';
  
  if (strength >= 100) {
    label = 'Stark';
    color = 'bg-green-500';
  } else if (strength >= 75) {
    label = 'Gut';
    color = 'bg-blue-500';
  } else if (strength >= 50) {
    label = 'Mittel';
    color = 'bg-amber-500';
  } else if (strength >= 25) {
    label = 'Schwach';
    color = 'bg-orange-500';
  }
  
  return { strength, label, color, feedback };
};

const AdminUsers = ({ scope = 'mitarbeiter', embedded = false }) => {
  const { isAdmin, isFullAccess, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [archiveDialog, setArchiveDialog] = useState({ open: false, id: null, name: '' });
  const [unarchiveDialog, setUnarchiveDialog] = useState({ open: false, id: null, name: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, id: null, name: '' });
  const [accessCodeResult, setAccessCodeResult] = useState(null); // { code, valid_minutes }
  const [generatingCode, setGeneratingCode] = useState(false);
  const [linkResultDialog, setLinkResultDialog] = useState({ open: false, title: '', email: '', link: '', emailSent: false, sentAt: '' });
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [sitzungenFrage, setSitzungenFrage] = useState(false);
  const [beendeSitzungen, setBeendeSitzungen] = useState(false);
  const [generatingPassword, setGeneratingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [onlyNeverLoggedIn, setOnlyNeverLoggedIn] = useState(false);
  // Z3 (nur Verwaltung): Zusatzfilter 'gesperrt' | 'ohne2fa', Konto-Dialoge, Pflicht-Karte neu laden, Detail-Blatt
  const [zusatzFilter, setZusatzFilter] = useState(null);
  const [kontoDialog, setKontoDialog] = useState({ art: null, konto: null });
  const [pflichtVersion, setPflichtVersion] = useState(0);
  const [alleVerwaltung, setAlleVerwaltung] = useState([]);
  const [detailId, setDetailId] = useState(null);
  // F11: Zugangs-Übersicht (id -> dringend)
  const [noAccessMap, setNoAccessMap] = useState(new Map());
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('filter') === 'never-logged-in') { setOnlyNeverLoggedIn(true); setZusatzFilter(null); }
  }, [searchParams]);

  // Deep-link: scroll to & highlight a focused user from global search
  useEffect(() => {
    const focusId = searchParams.get('focus');
    if (!focusId) return;
    setStatusFilter('all');
    setZusatzFilter(null);
    let tries = 0;
    const tick = () => {
      const el = document.querySelector(`[data-testid="user-row-${focusId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2', 'bg-blue-50', 'transition');
        setTimeout(() => el.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2', 'bg-blue-50'), 2500);
        const next = new URLSearchParams(searchParams);
        next.delete('focus'); next.delete('tab');
        setSearchParams(next, { replace: true });
        return;
      }
      if (tries++ < 15) setTimeout(tick, 200);
    };
    const t = setTimeout(tick, 300);
    return () => clearTimeout(t);
  }, [searchParams]);
  
  // All available roles
  const ROLES = [
    { value: 'mitarbeiter', label: 'Mitarbeiter' },
    { value: 'admin', label: 'Administrator' },
    { value: 'backoffice', label: 'Backoffice' },
    { value: 'personalmanager', label: 'Personalmanager' },
    { value: 'koordinator', label: 'Koordinator' },
    { value: 'geschaeftsfuehrung', label: 'Geschäftsführung' },
  ];
  
  // Roles that non-admin users can create (only mitarbeiter)
  const ALLOWED_ROLES_FOR_NON_ADMIN = [
    { value: 'mitarbeiter', label: 'Mitarbeiter' },
  ];
  
  const getRoleLabel = (role) => {
    const found = ROLES.find(r => r.value === role);
    return found ? found.label : role;
  };
  
  const isAdminLikeRole = (role) => {
    return ['admin', 'backoffice', 'personalmanager', 'koordinator', 'geschaeftsfuehrung'].includes(role);
  };
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'mitarbeiter',
  });
  
  const [resetPassword, setResetPassword] = useState('');
  
  const passwordStrength = useMemo(() => {
    return checkPasswordStrength(formData.password);
  }, [formData.password]);
  
  const resetPasswordStrength = useMemo(() => {
    return checkPasswordStrength(resetPassword);
  }, [resetPassword]);

  useEffect(() => {
    fetchUsers();
  }, [statusFilter]);

  // F11: Zugangs-Status laden (dringend/unklar + count_dringend)
  const fetchOnboarding = async () => {
    try {
      const { data } = await usersAPI.onboardingStatus();
      const m = new Map();
      (data.users || []).forEach(u => m.set(u.id, u.dringend === true));
      setNoAccessMap(m);
    } catch {
      /* stumm: bei Fehler keine Badges */
    }
  };
  useEffect(() => {
    fetchOnboarding();
  }, []);

  const fetchUsers = async ({ leise = false } = {}) => {
    try {
      if (!leise) setLoading(true);
      
      if (isFullAccess()) {
        const allResponse = await usersAPI.getAllRoles();
        const allUsers = allResponse.data;
        
        // Apply status filter on admin-like users too
        const filteredAll = statusFilter === 'all' ? allUsers :
          statusFilter === 'archived' ? allUsers.filter(u => u.is_active === false) :
          allUsers.filter(u => u.is_active !== false);
        
        const mitarbeiter = filteredAll.filter(u => u.role === 'mitarbeiter');
        const admins = filteredAll.filter(u => isAdminLikeRole(u.role));
        
        setUsers(mitarbeiter);
        setAdminUsers(admins);
        setAlleVerwaltung(allUsers.filter(u => isAdminLikeRole(u.role)));
      } else {
        const response = await usersAPI.getAll(statusFilter);
        setUsers(response.data);
        setAdminUsers([]);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  // Verwaltung: nach jeder Aktion ruhig neu laden (ohne Skeleton), dazu Zugangs-Übersicht und Pflicht-Zählung.
  const neuLaden = () => {
    if (scope !== 'verwaltung') return fetchUsers();
    fetchUsers({ leise: true });
    fetchOnboarding();
    setPflichtVersion(v => v + 1);
  };

  const oeffnePasswort = (user) => {
    setResetPasswordDialog({ open: true, id: user.id, name: `${user.first_name} ${user.last_name}` });
    setResetPassword('');
    setShowResetPassword(false);
    setAccessCodeResult(null);
  };

  const onKontoAktion = (key, user) => {
    const name = `${user.first_name} ${user.last_name}`;
    if (key === 'bearbeiten') return handleEdit(user);
    if (key === 'wiederherstellen') return setUnarchiveDialog({ open: true, id: user.id, name, gesperrt: istGesperrt(user) });
    if (key === 'loeschen') return setDeleteDialog({ open: true, id: user.id, name });
    if (key === 'archivieren') return setArchiveDialog({ open: true, id: user.id, name, gesperrt: istGesperrt(user) });
    if (key === 'passwort') return oeffnePasswort(user);
    return setKontoDialog({ art: key, konto: user });
  };

  const zusatzUmschalten = (filter) => {
    setOnlyNeverLoggedIn(false);
    setZusatzFilter(f => (f === filter ? null : filter));
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: scope === 'verwaltung' ? 'koordinator' : 'mitarbeiter',
    });
    setShowPassword(false);
    setShowDialog(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.email || !formData.first_name || !formData.last_name) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    
    if (!editingUser && formData.password && passwordStrength.strength < 50) {
      toast.error('Bitte ein stärkeres Passwort wählen');
      return;
    }

    try {
      setSaving(true);
      if (editingUser) {
        const updateData = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          role: formData.role,
        };
        await usersAPI.update(editingUser.id, updateData);
        toast.success('Benutzer aktualisiert');
      } else {
        const createData = {
          ...formData,
          password: formData.password || '',
        };
        const response = await usersAPI.create(createData);
        const data = response.data;
        setLinkResultDialog({
          open: true,
          title: 'Einladung erstellt',
          email: data.email,
          link: data.invite_link || '',
          emailSent: !!data.email_sent,
          sentAt: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        });
      }
      setShowDialog(false);
      neuLaden();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Speichern'));
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    try {
      setArchiving(true);
      await usersAPI.archive(archiveDialog.id);
      toast.success(scope === 'verwaltung' ? 'Konto archiviert' : 'Mitarbeiter archiviert');
      setArchiveDialog({ open: false, id: null, name: '' });
      neuLaden();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Archivieren'));
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchive = async () => {
    try {
      setArchiving(true);
      await usersAPI.unarchive(unarchiveDialog.id);
      toast.success(scope === 'verwaltung' ? 'Konto wiederhergestellt' : 'Mitarbeiter reaktiviert');
      setUnarchiveDialog({ open: false, id: null, name: '' });
      neuLaden();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Reaktivieren'));
    } finally {
      setArchiving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await usersAPI.delete(deleteDialog.id);
      toast.success('Benutzer endgültig gelöscht');
      setDeleteDialog({ open: false, id: null, name: '' });
      neuLaden();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Löschen'));
    } finally {
      setDeleting(false);
    }
  };
  
  const handleGenerateAccessCode = async () => {
    try {
      setGeneratingCode(true);
      const response = await usersAPI.createAccessCode(resetPasswordDialog.id);
      setAccessCodeResult({ code: response.data.code, valid_minutes: response.data.valid_minutes });
      if (scope === 'verwaltung') neuLaden();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Erzeugen des Zugangscodes'));
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleSitzungenBeenden = async (e) => {
    e.preventDefault();
    try {
      setBeendeSitzungen(true);
      await usersAPI.sitzungenBeenden(resetPasswordDialog.id);
      setSitzungenFrage(false);
      toast.success('Auf allen Geräten abgemeldet.');
    } catch (error) {
      toast.error(fehlertext(error, 'Abmelden auf allen Geräten fehlgeschlagen'));
    } finally {
      setBeendeSitzungen(false);
    }
  };

  const handleSendResetLink = async () => {
    try {
      setResettingPassword(true);
      const targetUser = [...users, ...adminUsers].find(u => u.id === resetPasswordDialog.id);
      const response = await usersAPI.sendResetLink(resetPasswordDialog.id);
      const data = response.data;
      setResetPasswordDialog({ open: false, id: null, name: '' });
      setLinkResultDialog({
        open: true,
        title: 'Reset-Link erstellt',
        email: targetUser?.email || '',
        link: data.reset_link || '',
        emailSent: !!data.email_sent,
        sentAt: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
      });
      if (scope === 'verwaltung') neuLaden();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Erstellen des Reset-Links'));
    } finally {
      setResettingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Render user table helper function
  const renderUserTable = (userList, tableType) => {
    if (userList.length === 0) {
      return (
        <div className="text-center py-8 text-slate-500">
          {onlyNeverLoggedIn
            ? 'Alle Konten in dieser Ansicht haben einen nachweisbaren Zugang.'
            : statusFilter === 'archived'
              ? 'Keine archivierten Benutzer vorhanden'
              : tableType === 'mitarbeiter'
                ? 'Keine Mitarbeiter vorhanden'
                : 'Keine verwaltenden Benutzer vorhanden'}
        </div>
      );
    }
    
    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>E-Mail</TableHead>
              <TableHead>Rolle</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {userList.map((user) => {
              const isArchived = user.is_active === false;
              const canManageRow = isAdmin() || user.role !== 'admin';
              return (
              <TableRow key={user.id} data-testid={`user-row-${user.id}`} className={isArchived ? 'opacity-60' : ''}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center ${
                      isArchived ? 'bg-slate-200' :
                      isAdminLikeRole(user.role) ? 'bg-blue-100' : 'bg-slate-100'
                    }`}>
                      {isAdminLikeRole(user.role) 
                        ? <Shield className="h-4 w-4 text-blue-800" />
                        : <User className="h-4 w-4 text-slate-600" />
                      }
                    </div>
                    <span className="font-medium text-slate-900">
                      {user.first_name} {user.last_name}
                    </span>
                    {noAccessMap.has(user.id) && !isArchived && (() => {
                      const dringend = noAccessMap.get(user.id) === true;
                      return (
                      <button
                        type="button"
                        onClick={() => setResetPasswordDialog({ open: true, id: user.id, name: `${user.first_name} ${user.last_name}` })}
                        className={`inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 font-medium ${
                          dringend
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        title={dringend
                          ? 'Kein Zugang nachweisbar — klicken, um Zugang einzurichten'
                          : 'Kein Zugang nachweisbar (unklarer Altfall) — klicken, um Zugang einzurichten'}
                        data-testid={`never-logged-in-badge-${user.id}`}
                      >
                        Kein Zugang nachweisbar
                        {user.created_at && (() => {
                          const d = Math.floor((Date.now() - new Date(user.created_at).getTime()) / 86400000);
                          return d >= 0 ? <span className="opacity-70">· seit {d} Tagen</span> : null;
                        })()}
                      </button>
                      );
                    })()}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 text-slate-500">
                    <Mail className="h-4 w-4" />
                    {user.email}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={isAdminLikeRole(user.role) ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}>
                    {getRoleLabel(user.role)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {isArchived ? (
                    <Badge className="bg-slate-200 text-slate-600" data-testid={`status-badge-${user.id}`}>
                      <Archive className="h-3 w-3 mr-1" />
                      Archiviert
                    </Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-700" data-testid={`status-badge-${user.id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Aktiv
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {canManageRow && (
                      <>
                        {!isArchived && (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(user)}
                                  data-testid={`edit-user-${user.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Benutzer bearbeiten</p>
                              </TooltipContent>
                            </Tooltip>
                            
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setResetPasswordDialog({ 
                                      open: true, 
                                      id: user.id, 
                                      name: `${user.first_name} ${user.last_name}` 
                                    });
                                    setResetPassword('');
                                    setShowResetPassword(false);
                                    setAccessCodeResult(null);
                                  }}
                                  data-testid={`reset-password-${user.id}`}
                                >
                                  <Key className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Passwort zurücksetzen</p>
                              </TooltipContent>
                            </Tooltip>
                          </>
                        )}
                        
                        {user.id !== currentUser?.id && (
                          <>
                            {isArchived ? (
                              <>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-green-600 hover:text-green-700"
                                      onClick={() => setUnarchiveDialog({ open: true, id: user.id, name: `${user.first_name} ${user.last_name}` })}
                                      data-testid={`unarchive-user-${user.id}`}
                                    >
                                      <ArchiveRestore className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Wiederherstellen</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600 hover:text-red-700"
                                      onClick={() => setDeleteDialog({ open: true, id: user.id, name: `${user.first_name} ${user.last_name}` })}
                                      data-testid={`delete-user-${user.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Endgültig löschen</p>
                                  </TooltipContent>
                                </Tooltip>
                              </>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-amber-600 hover:text-amber-700"
                                    onClick={() => setArchiveDialog({ open: true, id: user.id, name: `${user.first_name} ${user.last_name}` })}
                                    data-testid={`archive-user-${user.id}`}
                                  >
                                    <Archive className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Archivieren</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className={embedded ? 'space-y-4' : 'space-y-6'} data-testid="admin-users-page">
        {/* Header */}
        {!embedded && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-[Manrope]">
                Mitarbeiterverwaltung
              </h1>
              <p className="text-slate-500 mt-1">
                Verwalten Sie Benutzer und Zugänge
              </p>
            </div>
            
            <Button
              className="bg-blue-800 hover:bg-blue-900"
              onClick={handleCreate}
              data-testid="create-user-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              {scope === 'verwaltung' ? 'Neuer Benutzer' : 'Neuer Mitarbeiter'}
            </Button>
          </div>
        )}

        {embedded && (
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">
              {scope === 'verwaltung' ? 'Verwaltungskonten' : 'Mitarbeiter'}
            </h3>
            <Button
              className="bg-blue-800 hover:bg-blue-900"
              onClick={handleCreate}
              data-testid="create-user-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              {scope === 'verwaltung' ? 'Neuer Benutzer' : 'Neuer Mitarbeiter'}
            </Button>
          </div>
        )}

        {/* Status Filter */}
        {scope === 'verwaltung' && (
          <PflichtKarte
            version={pflichtVersion}
            onZaehlungKlick={() => { setStatusFilter('active'); setOnlyNeverLoggedIn(false); setZusatzFilter('ohne2fa'); }}
          />
        )}

        <div className={`flex items-center gap-2${scope === 'verwaltung' ? ' flex-wrap' : ''}`} data-testid="status-filter">
          <span className="text-sm text-slate-500">Anzeigen:</span>
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${statusFilter === 'active' ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="filter-active"
            >
              Aktive
            </button>
            <button
              onClick={() => { setStatusFilter('archived'); setOnlyNeverLoggedIn(false); setZusatzFilter(null); }}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${statusFilter === 'archived' ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="filter-archived"
            >
              Archivierte
            </button>
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${statusFilter === 'all' ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="filter-all"
            >
              Alle
            </button>
          </div>
          {(() => {
            const baseList = scope === 'verwaltung' ? adminUsers : users;
            const ohneZugangHier = baseList.filter(
              u => noAccessMap.has(u.id) && u.is_active !== false
            ).length;
            const dringendHier = baseList.filter(
              u => noAccessMap.get(u.id) === true && u.is_active !== false
            ).length;
            if (ohneZugangHier === 0 || statusFilter === 'archived') return null;
            return (
              <button
                onClick={() => { setOnlyNeverLoggedIn(v => !v); setZusatzFilter(null); }}
                className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${onlyNeverLoggedIn ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-200 hover:bg-red-50'}`}
                data-testid="filter-never-logged-in"
                title="Konten ohne Anmeldung seit dem Update erscheinen hier, bis sie sich einmal angemeldet haben."
              >
                Kein Zugang nachweisbar ({ohneZugangHier}{dringendHier > 0 ? `, ${dringendHier} dringend` : ''})
              </button>
            );
          })()}
          {scope === 'verwaltung' && statusFilter !== 'archived' && (() => {
            const anzahl = zusatzAnzahlen(adminUsers);
            const knopf = (filter, text, n) => (n > 0 || zusatzFilter === filter) && (
              <button
                key={filter}
                onClick={() => zusatzUmschalten(filter)}
                className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${zusatzFilter === filter ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-200 hover:bg-red-50'}`}
                data-testid={`filter-${filter}`}
              >
                {text} ({n})
              </button>
            );
            return [knopf('gesperrt', 'Gesperrt', anzahl.gesperrt), knopf('ohne2fa', 'Ohne 2FA', anzahl.ohne2fa)];
          })()}
        </div>

        {/* User List */}
        {(() => {
          const baseList = scope === 'verwaltung' ? adminUsers : users;
          const sichtbareListe = onlyNeverLoggedIn
            ? baseList.filter(u => noAccessMap.has(u.id) && u.is_active !== false)
            : (scope === 'verwaltung' && zusatzFilter ? baseList.filter(u => passtZusatzfilter(u, zusatzFilter)) : baseList);
          return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              {scope === 'verwaltung' ? <Shield className="h-5 w-5" /> : <Users className="h-5 w-5" />}
              {scope === 'verwaltung' ? 'Verwaltende Rollen' : 'Mitarbeiter'} ({sichtbareListe.length})
            </CardTitle>
            <CardDescription>
              {scope === 'verwaltung'
                ? 'Administratoren, Backoffice, Koordinatoren, Personalmanager und Geschäftsführung'
                : 'Schulbegleiter und ihre Zeiterfassung'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {scope !== 'verwaltung' ? renderUserTable(sichtbareListe, scope) : (sichtbareListe.length === 0 && zusatzFilter) ? (
              <div className="text-center py-8 text-slate-500" data-testid="verwaltung-leer">
                {zusatzFilter === 'gesperrt'
                  ? 'In dieser Ansicht ist kein Konto gesperrt.'
                  : 'Alle Konten in dieser Ansicht haben die Zwei-Faktor-Anmeldung eingerichtet.'}
              </div>
            ) : sichtbareListe.length === 0 ? renderUserTable(sichtbareListe, scope) : (
              <KontoTabelle
                liste={sichtbareListe}
                ich={currentUser}
                noAccessMap={noAccessMap}
                darfVerwalten={(u) => isAdmin() || u.role !== 'admin'}
                onKeinZugang={oeffnePasswort}
                onName={(u) => setDetailId(u.id)}
                onAktion={onKontoAktion}
              />
            )}
          </CardContent>
        </Card>
          );
        })()}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer erstellen'}
              </DialogTitle>
              <DialogDescription>
                {editingUser
                  ? 'Aktualisieren Sie die Benutzerdaten'
                  : 'Erstellen Sie einen neuen Mitarbeiter-Zugang'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">Vorname *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="Max"
                    data-testid="user-first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Nachname *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Mustermann"
                    data-testid="user-last-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">E-Mail *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="max@beispiel.de"
                  data-testid="user-email-input"
                />
              </div>

              <div>
                <Label>Rolle</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  disabled={(!isFullAccess() && editingUser) || Boolean(editingUser?.gesperrt)}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(isAdmin() ? ROLES : (isFullAccess() ? ROLES.filter((r) => r.value !== 'admin') : ALLOWED_ROLES_FOR_NON_ADMIN)).map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editingUser?.gesperrt && (
                  <p className="text-xs text-amber-700 mt-1" data-testid="rolle-gesperrt-hinweis">Die Rolle lässt sich erst nach dem Entsperren ändern.</p>
                )}
                {isFullAccess() ? (
                  <p className="text-xs text-slate-500 mt-1">
                    Admin: Voller Zugriff | Backoffice/PM/Koordinator/GF: Ohne Systemeinstellungen | Mitarbeiter: Nur eigene Zeiten
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">
                    Sie können nur Mitarbeiter anlegen
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Abbrechen
              </Button>
              <Button
                className="bg-blue-800 hover:bg-blue-900"
                onClick={handleSave}
                disabled={saving}
                data-testid="save-user-button"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {editingUser ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog (Link-Versand) */}
        <Dialog open={resetPasswordDialog.open} onOpenChange={(open) => setResetPasswordDialog(prev => ({ ...prev, open }))}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Passwort zurücksetzen</DialogTitle>
              <DialogDescription>
                {resetPasswordDialog.name} erhält per E-Mail einen Link, um sich selbst ein neues Passwort zu vergeben. Es wird kein Passwort verschickt oder angezeigt.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
              Der Link ist 72 Stunden gültig. Ältere Links des Mitarbeiters bleiben ebenfalls
              gültig — es funktioniert immer die neueste E-Mail.
            </div>

            {accessCodeResult ? (
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 space-y-2" data-testid="access-code-result">
                <p className="text-xs text-slate-500">Zugangscode (noch {accessCodeResult.valid_minutes} Minuten gültig):</p>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-mono font-bold tracking-widest text-blue-900" data-testid="access-code-value">{accessCodeResult.code}</span>
                  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard?.writeText(accessCodeResult.code); toast.success('Kopiert'); }} data-testid="copy-access-code">
                    Kopieren
                  </Button>
                </div>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Diesen Code bitte <strong>telefonisch</strong> durchsagen. Nicht per WhatsApp, SMS oder E-Mail
                  verschicken — er ist ein Zugangsschlüssel zu Sozialdaten.
                </p>
                <p className="text-xs text-slate-500">
                  Der/die Mitarbeiter:in löst ihn auf der Login-Seite unter „Ich habe einen Zugangscode" ein.
                </p>
              </div>
            ) : (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleGenerateAccessCode}
                disabled={generatingCode}
                data-testid="generate-access-code-button"
              >
                {generatingCode ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Zugangscode erzeugen (für telefonische Durchsage)
              </Button>
            )}

            {scope !== 'verwaltung' && (
              <div className="space-y-1.5">
                <Button variant="outline" className="w-full" onClick={() => setSitzungenFrage(true)} data-testid="mitarbeiter-sitzungen-beenden">
                  Auf allen Geräten abmelden
                </Button>
                <p className="text-xs text-slate-500" data-testid="mitarbeiter-sitzungen-hinweis">
                  Zum Beispiel, wenn das Handy verloren ging. Das Passwort bleibt gleich; bei einem verlorenen Handy zusätzlich einen Passwort-Link senden.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => { setResetPasswordDialog(prev => ({ ...prev, open: false })); setAccessCodeResult(null); }}>
                Schließen
              </Button>
              <Button
                className="bg-blue-800 hover:bg-blue-900"
                onClick={handleSendResetLink}
                disabled={resettingPassword}
                data-testid="confirm-reset-password-button"
              >
                {resettingPassword ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Reset-Link per E-Mail senden
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Gemeinsamer Ergebnis-Dialog (Reset + Einladung) */}
        <Dialog open={linkResultDialog.open} onOpenChange={(open) => setLinkResultDialog(prev => ({ ...prev, open }))}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{linkResultDialog.title}</DialogTitle>
              <DialogDescription>
                {linkResultDialog.email} kann sich darüber selbst ein Passwort vergeben.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {linkResultDialog.emailSent ? (
                <div className="flex items-start gap-2 rounded-lg bg-green-50 border border-green-200 p-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-800">E-Mail gesendet an {linkResultDialog.email}{linkResultDialog.sentAt ? ` um ${linkResultDialog.sentAt} Uhr` : ''}</p>
                </div>
              ) : (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
                  <XCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">E-Mail konnte nicht versendet werden — bitte den Link unten manuell weitergeben</p>
                </div>
              )}

              {linkResultDialog.link && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <p className="text-xs text-slate-500 mb-1">Link</p>
                  <p className="text-xs text-slate-900 font-mono break-all">{linkResultDialog.link}</p>
                </div>
              )}

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  navigator.clipboard.writeText(linkResultDialog.link).then(() => {
                    toast.success('Link in die Zwischenablage kopiert');
                  });
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Link kopieren
              </Button>
            </div>

            <DialogFooter>
              <Button
                className="w-full bg-blue-800 hover:bg-blue-900"
                onClick={() => setLinkResultDialog({ open: false, title: '', email: '', link: '', emailSent: false, sentAt: '' })}
              >
                Fertig
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={sitzungenFrage} onOpenChange={(open) => { if (!beendeSitzungen) setSitzungenFrage(open); }}>
          <AlertDialogContent data-testid="mitarbeiter-sitzungen-rueckfrage">
            <AlertDialogHeader>
              <AlertDialogTitle>Auf allen Geräten abmelden?</AlertDialogTitle>
              <AlertDialogDescription>
                {resetPasswordDialog.name} wird auf allen Geräten abgemeldet und muss sich neu anmelden. Das Passwort bleibt gleich.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={beendeSitzungen} data-testid="mitarbeiter-sitzungen-abbrechen">Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={handleSitzungenBeenden} disabled={beendeSitzungen} className="bg-red-600 hover:bg-red-700" data-testid="mitarbeiter-sitzungen-bestaetigen">
                {beendeSitzungen ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Abmelden
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Archive Confirmation */}
        <AlertDialog open={archiveDialog.open} onOpenChange={(open) => setArchiveDialog(prev => ({ ...prev, open }))}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{scope === 'verwaltung' ? 'Konto archivieren?' : 'Mitarbeiter archivieren?'}</AlertDialogTitle>
              <AlertDialogDescription>
                Der Zugang von "{archiveDialog.name}" wird deaktiviert. Alle Daten (Stundenzettel, Zuordnungen) bleiben vollständig erhalten.
                Der Mitarbeiter kann sich nicht mehr anmelden, bis er reaktiviert wird.
              </AlertDialogDescription>
              {scope === 'verwaltung' && !archiveDialog.gesperrt && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="archiv-tipp">
                  Nur vorübergehend? Dann lieber „Sperren" – das Konto bleibt in der Liste und lässt sich jederzeit entsperren.
                </p>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleArchive}
                disabled={archiving}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-archive-button"
              >
                {archiving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Archive className="h-4 w-4 mr-2" />}
                Archivieren
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Unarchive Confirmation */}
        <AlertDialog open={unarchiveDialog.open} onOpenChange={(open) => setUnarchiveDialog(prev => ({ ...prev, open }))}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{scope === 'verwaltung' ? 'Konto wiederherstellen?' : 'Mitarbeiter reaktivieren?'}</AlertDialogTitle>
              <AlertDialogDescription data-testid="unarchive-text">
                Der Zugang von "{unarchiveDialog.name}" wird wiederhergestellt. {unarchiveDialog.gesperrt
                  ? 'Das Konto bleibt gesperrt. Entsperren Sie es danach, damit sich die Person wieder anmelden kann.'
                  : 'Der Mitarbeiter kann sich danach wieder anmelden.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleUnarchive}
                disabled={archiving}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-unarchive-button"
              >
                {archiving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArchiveRestore className="h-4 w-4 mr-2" />}
                {scope === 'verwaltung' ? 'Wiederherstellen' : 'Reaktivieren'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {scope === 'verwaltung' && (
          <KontoDetails
            konto={alleVerwaltung.find(u => u.id === detailId) || null}
            onClose={() => setDetailId(null)}
            ich={currentUser}
            noAccessMap={noAccessMap}
            onAktion={onKontoAktion}
            version={pflichtVersion}
          />
        )}

        {scope === 'verwaltung' && (
          <KontoAktionDialog
            art={kontoDialog.art}
            konto={kontoDialog.konto}
            onClose={() => setKontoDialog({ art: null, konto: null })}
            onErledigt={neuLaden}
          />
        )}

        {/* Delete Confirmation (only for archived users) */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog(prev => ({ ...prev, open }))}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Benutzer endgültig löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie den archivierten Benutzer "{deleteDialog.name}" wirklich endgültig löschen?
                Alle zugehörigen Zuordnungen und Stundenzettel werden unwiderruflich gelöscht.
                Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-button"
              >
                {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Endgültig löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
};

export default AdminUsers;
